import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api } from "../../api/client";
import { apiError } from "../../lib/apiError";
import {
  DEFAULT_TAG_DISPLAY,
  formatEarTag,
  matchOfficialEarTag,
  normalizeTagDisplayPreferences,
  type TagDisplayFormat,
  type TagDisplayPreferences,
} from "../../lib/tagDisplay";

const STORAGE_KEY = "herd:tag-display";

type TagDisplayApi = {
  preferences: TagDisplayPreferences;
  save: (next: TagDisplayPreferences) => Promise<void>;
};

const TagDisplayContext = createContext<TagDisplayApi | null>(null);

function readStored(): TagDisplayPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_TAG_DISPLAY;
    }
    return normalizeTagDisplayPreferences(JSON.parse(raw));
  } catch {
    return DEFAULT_TAG_DISPLAY;
  }
}

function writeStored(preferences: TagDisplayPreferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

export function clearStoredTagDisplay(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function TagDisplayProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<TagDisplayPreferences>(readStored);

  useEffect(() => {
    let cancelled = false;
    void api.GET("/api/settings/tag-display").then((result) => {
      if (cancelled || result.error || result.data == null) {
        return;
      }
      const next = normalizeTagDisplayPreferences(result.data);
      setPreferences(next);
      writeStored(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (next: TagDisplayPreferences) => {
    const previous = preferences;
    setPreferences(next);
    writeStored(next);
    const result = await api.PUT("/api/settings/tag-display", { body: next });
    if (result.error) {
      setPreferences(previous);
      writeStored(previous);
      throw new Error(apiError(result.error, "Nie udało się zapisać formatu kolczyków."));
    }
    const saved = normalizeTagDisplayPreferences(result.data ?? next);
    setPreferences(saved);
    writeStored(saved);
  }, [preferences]);

  const value = useMemo(() => ({ preferences, save }), [preferences, save]);
  return <TagDisplayContext.Provider value={value}>{children}</TagDisplayContext.Provider>;
}

function useTagDisplay(): TagDisplayApi {
  const value = useContext(TagDisplayContext);
  if (!value) {
    return {
      preferences: DEFAULT_TAG_DISPLAY,
      save: async () => {
        throw new Error("Brak ustawień prezentacji kolczyków.");
      },
    };
  }
  return value;
}

export function useFormatEarTag(): (value: string | null | undefined) => string {
  const { preferences } = useTagDisplay();
  return (value) => {
    if (!value) {
      return "";
    }
    return formatEarTag(value, preferences.display_format);
  };
}

export function useTagDisplayPreferences(): TagDisplayApi {
  return useTagDisplay();
}

type EarTagViewProps = {
  value: string;
  format: TagDisplayFormat;
  boldLast: boolean;
};

/** Widok numeru przy zadanych opcjach — podgląd w ustawieniach i listy w aplikacji. */
export function EarTagView({ value, format, boldLast }: EarTagViewProps) {
  const compact = matchOfficialEarTag(value);
  const text = compact ? formatEarTag(compact, format) : value;
  const markLast = Boolean(compact && boldLast && /\d$/.test(text));
  if (!markLast) {
    return (
      <span className="ear-tag" {...(compact ? { "aria-label": compact } : {})}>
        {text}
      </span>
    );
  }
  return (
    <span className="ear-tag" aria-label={compact ?? undefined}>
      {text.slice(0, -1)}
      <span className="ear-tag__check">{text.slice(-1)}</span>
    </span>
  );
}

export function EarTag({ value }: { value: string | null | undefined }) {
  const { preferences } = useTagDisplay();
  if (!value) {
    return null;
  }
  return (
    <EarTagView
      value={value}
      format={preferences.display_format}
      boldLast={preferences.bold_last_digit}
    />
  );
}

const OFFICIAL_TAG_RE = /(PL1000\d{8})/i;

/** Wstawia format kolczyka w zwykły tekst (tytuły, aktywność). */
export function FormattedTags({ text }: { text: string }) {
  const parts = text.split(OFFICIAL_TAG_RE);
  return (
    <>
      {parts.map((part, index) =>
        /^PL1000\d{8}$/i.test(part) ? (
          <EarTag key={`${part}-${index}`} value={part} />
        ) : (
          <span key={`text-${index}`}>{part}</span>
        ),
      )}
    </>
  );
}
